import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { getModifierByDisciplineCode } from "../../afinanceapi";

export default function AddModifierModal({
  show,
  onHide,
  practiceDisciplineCode,
  invoiceLines = [],
  onSave,
  setLines,
}) {
  const [modifiers, setModifiers] = useState([]);
  const [selectedModifier, setSelectedModifier] = useState("");
  const [modifierDesc, setModifierDesc] = useState("");
  const [checkedLines, setCheckedLines] = useState({});
  const [newRates, setNewRates] = useState({});
  const [lineModifiers, setLineModifiers] = useState({});
  const [modifierRates, setModifierRates] = useState({});

  // ── helpers: classification
  const isNappiLine = (l) => l?.type === "nappi" || !!l?.nappiCode;
  const isTariffLine = (l) => !isNappiLine(l);

  // selected modifier description
  useEffect(() => {
    const mod = modifiers.find((m) => m.code === selectedModifier);
    setModifierDesc(mod ? mod.description : "");
  }, [selectedModifier, modifiers]);

  // fetch modifiers for discipline - only when modal is shown and discipline code is available
  useEffect(() => {
    const fetchModifiers = async () => {
      try {
        if (!show || !practiceDisciplineCode) {
          if (!practiceDisciplineCode) setModifiers([]);
          return;
        }
        const response = await getModifierByDisciplineCode(practiceDisciplineCode);
        if (!response) throw new Error("Failed to fetch modifiers");
        setModifiers(response);
      } catch (error) {
        console.error("Error fetching modifiers:", error);
        setModifiers([]);
      }
    };
    fetchModifiers();
  }, [practiceDisciplineCode, show]);

  // initialize existing line modifiers (by code) – keep for both line types, but only tariffs can be changed
  useEffect(() => {
    const initial = {};
    invoiceLines.forEach((line) => {
      if (line.modifier) {
        const list = Array.isArray(line.modifier) ? line.modifier : [line.modifier];
        const codes = list
          .map((m) => (typeof m === "string" ? m : m.code))
          .filter(Boolean);
        initial[line.lineNumber] = codes;
      } else {
        initial[line.lineNumber] = [];
      }
    });
    setLineModifiers(initial);
  }, [invoiceLines]);

  // compute preview new rate only for checked **tariff** lines
  useEffect(() => {
    const mod = modifiers.find((m) => m.code === selectedModifier);
    if (!mod) return;

    let rawPct = mod.adjustmentPercentage;
    if (typeof rawPct === "string") rawPct = parseFloat(rawPct.replace("%", ""));
    const pct = (rawPct || 0) / 100;

    const nextNewRates = {};
    const nextModifierRates = {};
    const nextLineModifiers = { ...lineModifiers };

    Object.entries(checkedLines).forEach(([num, checked]) => {
      if (!checked) return;
      const lineNum = Number(num);
      const line = invoiceLines.find((l) => l.lineNumber === lineNum);
      if (!line || !isTariffLine(line)) return; // ⛔ skip NAPPI

      let baseRate = parseFloat(line.originalRate ?? line.rate ?? "0");
      if (Number.isNaN(baseRate)) baseRate = 0;

      let modifierRate = 0;
      if (mod.adjustment === "up") modifierRate = baseRate * pct;
      else if (mod.adjustment === "down") modifierRate = -baseRate * pct;

      const adjusted = baseRate + modifierRate;

      nextNewRates[lineNum] = Number(adjusted.toFixed(2));
      nextModifierRates[lineNum] = Number(modifierRate.toFixed(2));

      const existing = nextLineModifiers[lineNum] || [];
      if (!existing.includes(mod.code)) {
        nextLineModifiers[lineNum] = [...existing, mod.code];
      }
    });

    setNewRates(nextNewRates);
    setModifierRates(nextModifierRates);
    setLineModifiers(nextLineModifiers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifiers, selectedModifier, checkedLines, invoiceLines]);

  const handleLineToggle = (lineNumber) => {
    const line = invoiceLines.find((l) => l.lineNumber === lineNumber);
    if (!line || !isTariffLine(line)) return; // ⛔ prevent toggling NAPPI

    setCheckedLines((prev) => {
      const isNowChecked = !prev[lineNumber];
      const next = { ...prev, [lineNumber]: isNowChecked };
      if (!isNowChecked) {
        setNewRates((prevRates) => {
          const copy = { ...prevRates };
          delete copy[lineNumber];
          return copy;
        });
        setModifierRates((prevRates) => {
          const copy = { ...prevRates };
          delete copy[lineNumber];
          return copy;
        });
      }
      return next;
    });
  };

  const handleSave = () => {
    const mod = modifiers.find((m) => m.code === selectedModifier);
    if (!mod) return onHide();

    const pct =
      (typeof mod.adjustmentPercentage === "string"
        ? parseFloat(mod.adjustmentPercentage.replace("%", ""))
        : mod.adjustmentPercentage || 0) / 100;

    const updatedLines = invoiceLines.map((line) => {
      // ❌ never apply modifiers to NAPPI lines
      if (!isTariffLine(line)) return line;

      const isChecked = !!checkedLines[line.lineNumber];
      let baseRate = parseFloat(line.originalRate ?? line.rate ?? "0");
      if (Number.isNaN(baseRate)) baseRate = 0;

      const originalRate =
        line.originalRate ?? line.rate; // preserve the original

      const existingModifiers = Array.isArray(line.modifier) ? line.modifier : [];
      let modifierObjects = [...existingModifiers];

      if (isChecked) {
        const rateChange =
          mod.adjustment === "up"
            ? +(baseRate * pct).toFixed(2)
            : +(baseRate * -pct).toFixed(2);

      if (!modifierObjects.some((m) => m.code === mod.code)) {
  modifierObjects.push({
    code: mod.code,
    adjustment: mod.adjustment,                // "up" | "down"
    adjustmentPercentage: mod.adjustmentPercentage, // "10" or "10%"
    rateChange,
  });
} else {
  modifierObjects = modifierObjects.map((m) =>
    m.code === mod.code
      ? {
          ...m,
          adjustment: mod.adjustment,
          adjustmentPercentage: mod.adjustmentPercentage,
          rateChange,
        }
      : m
  );

}
      }

      const totalChange = modifierObjects.reduce(
        (sum, m) => sum + (m.rateChange || 0),
        0
      );
      const newRate = +(baseRate + totalChange).toFixed(2);

      return {
        ...line,
        rate: newRate,
        originalRate, // keep for DB
        modifier: modifierObjects,
      };
    });

    setLines?.(updatedLines);

    // pass back only checked tariff lines (1-based numbers)
    const selectedLineNumbers = Object.entries(checkedLines)
      .filter(([num, checked]) => {
        const ln = invoiceLines.find((l) => l.lineNumber === Number(num));
        return checked && ln && isTariffLine(ln);
      })
      .map(([num]) => Number(num));

    onSave?.(selectedModifier, selectedLineNumbers, {}, {});
    // reset UI
    setSelectedModifier("");
    setModifierDesc("");
    setCheckedLines({});
    setNewRates({});
    setModifierRates({});
    onHide();
  };

  const formatZAR = (value) =>
    new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" }).format(value);

  const displayRate = (line) => {
    const delta = modifierRates[line.lineNumber] ?? 0;
    const base = parseFloat(line.rate) || 0;
    return (base + delta).toFixed(2);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Add Modifier
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group as={Row} className="mb-4" controlId="modifierSelect">
            <Form.Label column sm={3}>Modifier</Form.Label>
            <Col sm={9}>
              <Form.Select
                value={selectedModifier}
                onChange={(e) => setSelectedModifier(e.target.value)}
              >
                <option value="">Select modifier...</option>
                {modifiers.map((mod) => (
                  <option key={mod.code} value={mod.code}>
                    {mod.code}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Form.Group>

          <Form.Group as={Row} controlId="modifierDescription">
            <Form.Label column sm={3}>Description</Form.Label>
            <Col sm={9}>
              <Form.Control as="textarea" rows={3} readOnly value={modifierDesc} />
            </Col>
          </Form.Group>
        </Form>

        <hr />

        <div>
          <h6 className="mb-1">Select Invoice Lines</h6>
          <Row className="fw-bold mb-2 text-center border-bottom">
            <Col xs={1}>Select</Col>
            <Col xs={1}>#</Col>
            <Col xs={2}>Tariff | Modifier</Col>
            <Col xs={3}>Description</Col>
            <Col xs={1}>Units</Col>
            <Col xs={2} className="text-end">Rate</Col>
            <Col xs={2} className="text-end">New Rate</Col>
          </Row>

          {invoiceLines.map((line) => {
            const nappi = isNappiLine(line);
            return (
              <Row key={line.lineNumber} className="align-items-center mb-2">
                <Col xs={1} className="text-center">
                  <Form.Check
                    type="checkbox"
                    id={`line-${line.lineNumber}`}
                    checked={!!checkedLines[line.lineNumber]}
                    onChange={() => handleLineToggle(line.lineNumber)}
                    disabled={nappi} // ⛔ cannot select NAPPI
                    title={nappi ? "Modifiers cannot be applied to NAPPI lines" : ""}
                  />
                </Col>
                <Col xs={1} className="text-center">#{line.lineNumber}</Col>
                <Col xs={2} className="text-center">
                  {nappi ? (
                    <span className="text-muted">NAPPI</span>
                  ) : (
                    <>
                      {line.tariffCode}
                      <br />
                      <small className="text-muted">
                        {(lineModifiers[line.lineNumber] || []).filter(Boolean).join(", ")}
                      </small>
                    </>
                  )}
                </Col>
                <Col xs={3}>{line.description}</Col>
                <Col xs={1} className="text-center">{line.quantity}</Col>
                <Col xs={2}>
                  <Form.Control
                    className="text-end"
                    plaintext
                    readOnly
                    value={formatZAR(line.rate)}
                  />
                </Col>
                <Col xs={2}>
                  <Form.Control
                    className="text-end"
                    plaintext
                    readOnly
                    value={
                      !nappi && newRates[line.lineNumber]
                        ? formatZAR(displayRate(line))
                        : ""
                    }
                  />
                </Col>
              </Row>
            );
          })}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!selectedModifier}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
